import type { i18n as I18nInstance, TFunction } from 'i18next';
import type { SubscriptionBlockViewDto } from '@subscriptions/application/dtos';

/** Resolved copy for the last-payment-error badge and description. */
export type LastPaymentErrorContent = {
  badgeText: string;
  description: string;
};

/**
 * Resolves the badge text and description for a failed last payment.
 *
 * The backend sends a flat error `code` (e.g. `insufficient_funds`,
 * `pin_try_exceeded`, `payment_failed`). The keys live under a fixed `payment`
 * nesting level, so they resolve to `lastPaymentStatus.codes.payment.${code}`
 * (e.g. `...codes.payment.payment_failed.badge`). When the `code` has no mapped
 * entry, it falls back to the generic badge copy and the backend-provided
 * `concept` as the description.
 *
 * The `badgeText` is reused in two places: as the status badge in the subscription
 * list (`SubscriptionPaymentInfo`) and as the error title in the block summary card
 * (`BlockLastPaymentStatusCard`).
 *
 * Adding support for a future `code` is therefore an i18n-only change: register a
 * new `lastPaymentStatus.codes.payment.<code>` entry — no component edits needed.
 *
 * @param lastError - Last payment error from the view DTO
 * @param t - i18next translation function bound to the `subscriptions` namespace
 * @param i18n - i18next instance used to probe for key existence
 * @returns Mapped `{ badgeText, description }`
 */
export const getLastPaymentErrorContent = (
  lastError: NonNullable<SubscriptionBlockViewDto['lastError']>,
  t: TFunction,
  i18n: I18nInstance,
): LastPaymentErrorContent => {
  const baseKey = `lastPaymentStatus.codes.payment.${lastError.code}`;

  if (i18n.exists(`subscriptions:${baseKey}.badge`)) {
    return {
      badgeText: t(`${baseKey}.badge`),
      description: t(`${baseKey}.description`),
    };
  }

  return {
    badgeText: t('lastPaymentStatus.fallbackBadge'),
    description: lastError.concept,
  };
};
